import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { getCategoryDisplayName } from '../config/categoryConfig';
import {
  answerLabel,
  buildDescription,
  formatAnswerDate,
  useYesterdayAnswers,
  YesterdayPair,
} from '../hooks/useYesterdayAnswers';

const SITE_URL = 'https://wallstreetle.com';
const PAGE_URL = `${SITE_URL}/answers`;
const TITLE = "Yesterday's Wallstreetle Answers and Compare the Pair";
const DESCRIPTION =
  "The answers to yesterday's free Wallstreetle puzzles: Household Name, Technology and WallStreetBets, plus Compare the Pair. New each day at 02:00 UTC.";

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Wallstreetle', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: "Yesterday's answers", item: PAGE_URL },
  ],
};

const CompanyName: React.FC<{ name: string | null; symbol: string }> = ({ name, symbol }) => {
  const label = answerLabel(name, symbol);
  if (label === symbol) return <span className="font-mono">{symbol}</span>;
  return (
    <>
      {label} <span className="font-mono text-base">({symbol})</span>
    </>
  );
};

const PairSection: React.FC<{ pair: YesterdayPair }> = ({ pair }) => (
  <section aria-labelledby="ctp-heading" className="mt-10">
    <h2 id="ctp-heading" className="font-serif text-2xl text-foreground mb-2">
      Compare the Pair: {answerLabel(pair.nameA, pair.symbolA)} vs {answerLabel(pair.nameB, pair.symbolB)}
    </h2>
    <p className="text-muted-foreground mb-4">
      Two companies from the same industry{pair.industry ? ` (${pair.industry})` : ''} went head to head.
      Here is the right answer to each question.
    </p>
    <Card className="p-5">
      <ol className="space-y-2 text-sm">
        {pair.questions.map(q => (
          <li key={q.metricKey} className="flex flex-wrap justify-between gap-x-4">
            <span className="text-muted-foreground">{q.question}</span>
            <span className="text-foreground font-medium">
              {q.winner === 'A'
                ? <CompanyName name={pair.nameA} symbol={pair.symbolA} />
                : <CompanyName name={pair.nameB} symbol={pair.symbolB} />}
            </span>
          </li>
        ))}
      </ol>
    </Card>
    <Link to="/compare-the-pair" className="inline-block mt-3 text-sm text-primary hover:underline">
      Play today&apos;s Compare the Pair
    </Link>
  </section>
);

/**
 * /answers: yesterday's answers for the free categories and yesterday's
 * Compare the Pair (owner decision, 2026-11-03: no history, no members-only
 * categories). The page moves on at the 02:00 UTC reset; today's puzzles are
 * never shown (see useYesterdayAnswers and the backend /api/answers/yesterday).
 */
export const AnswersPage: React.FC = () => {
  const { data, status } = useYesterdayAnswers();
  const hasContent = !!data && (data.answers.length > 0 || !!data.compareThePair);
  const longDate = data ? formatAnswerDate(data.date) : null;
  const description = data && data.answers.length > 0 ? buildDescription(data.date, data.answers) : DESCRIPTION;

  return (
    <>
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SITE_URL}/assets/share-link-image.png`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-background py-10 px-4">
        <div className="max-w-3xl mx-auto">
          <header className="mb-8">
            <h1 className="font-serif text-4xl text-foreground mb-3">
              Yesterday&apos;s Wallstreetle answers{hasContent && longDate ? `: ${longDate}` : ''}
            </h1>
            <p className="text-muted-foreground mb-2">
              Spoilers below. These are the answers to yesterday&apos;s free Wallstreetle puzzles and to
              yesterday&apos;s Compare the Pair. The page moves on to the next day at the 02:00 UTC reset.
            </p>
            <p className="text-muted-foreground">
              Today&apos;s puzzles are never shown here, so you can still{' '}
              <Link to="/start" className="text-primary hover:underline">play today&apos;s puzzles</Link> first.
            </p>
          </header>

          {status === 'loading' && <p className="text-muted-foreground" role="status">Loading yesterday&apos;s answers…</p>}

          {status === 'error' && (
            <p className="text-muted-foreground" role="alert">
              We couldn&apos;t load the answers just now. Please try again in a minute.
            </p>
          )}

          {status === 'ready' && !hasContent && (
            <p className="text-muted-foreground">
              Yesterday&apos;s answers aren&apos;t available right now. Check back after the next reset at 02:00 UTC.
            </p>
          )}

          {data && data.answers.length > 0 && (
            <div className="space-y-4">
              {data.answers.map(answer => {
                const categoryName = getCategoryDisplayName(answer.category);
                return (
                  <Card key={answer.category} className="p-5">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">{categoryName}</p>
                    <h2 className="text-xl font-semibold text-foreground">
                      <CompanyName name={answer.companyName} symbol={answer.symbol} />
                    </h2>
                    {answer.description && (
                      <p className="mt-2 text-sm text-muted-foreground">{answer.description}</p>
                    )}
                    <Link
                      to={`/${answer.category}`}
                      className="inline-block mt-3 text-sm text-primary hover:underline"
                    >
                      Play today&apos;s {categoryName} puzzle
                    </Link>
                  </Card>
                );
              })}
            </div>
          )}

          {data?.compareThePair && <PairSection pair={data.compareThePair} />}

          {hasContent && (
            <p className="mt-10 text-xs text-muted-foreground">
              Company descriptions and comparisons are for learning about the game&apos;s picks, not investment advice.
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default AnswersPage;
